import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Hero from "../../components/Hero";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./Search.module.scss";

const Search: FC = () => {
  return (
    <div className={styles.searchPage}>
      {/* 1. Hero */}
      <Hero
        eyebrow="Search"
        heading="Find the information you need."
        copy="When a building contains thousands of documents, drawings and records, finding the right information can take time. Pulse provides a structured way to search building information and get to the relevant document, drawing or asset more easily."
        backgroundImage={require("../../assets/images/search/search-hero.webp")}
        button={{
          text: "View the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />

      {/* 2. The Problem */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>The Challenge</span>
            <h2 className={styles.sectionHeading}>
              The information is there. Finding it shouldn't be difficult.
            </h2>
            <p className={styles.sectionCopy}>
              Large building information collections can contain thousands of
              documents, drawings and records. Finding the right information can
              mean searching folders, opening PDFs and remembering filenames.
            </p>
            <p className={styles.sectionCopy}>
              Pulse provides a single search experience across the information
              held within the platform.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/search/search-problem.webp")}
              alt="Scattered files vs Pulse structured search"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 3. Search Building Information */}
      <Section bgColor="white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Unified Search</span>
          <h2 className={styles.sectionHeading}>
            One search across the building.
          </h2>
          <p className={styles.sectionCopy}>
            Pulse brings together disparate technical records into a unified,
            fast search experience.
          </p>
        </div>
        <div className={styles.cardsGrid}>
          <div className={styles.cardItem}>
            <h3>
              <span>📁</span> Documents & Drawings
            </h3>
            <p>Find technical documents, O&M manuals and CAD/PDF drawings.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>
              <span>⚡</span> Assets
            </h3>
            <p>Find equipment, plant records and associated metadata.</p>
          </div>
          <div className={styles.cardItem}>
            <h3>
              <span>🏢</span> Building Information
            </h3>
            <p>
              Search structured building records, specifications and
              certificates.
            </p>
          </div>
        </div>
        <div style={{ marginTop: "4rem" }}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/search/search-product.webp")}
              alt="Pulse search product interface"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 4. Benefits */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Benefits</span>
          <h2 className={styles.sectionHeading}>
            Make building information easier to find.
          </h2>
          <p className={styles.sectionCopy}>
            Pulse helps building teams spend less time looking for information
            and more time using it.
          </p>
        </div>
        <div className={styles.benefitGrid}>
          <div className={styles.benefitCard}>
            <h3>Find information faster</h3>
            <p>
              Reduce the time spent browsing folders and manually locating
              documents.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Search across the building record</h3>
            <p>
              Bring different types of building information into one search
              experience.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Give results useful context</h3>
            <p>
              Use the information associated with building records to help users
              understand what they have found.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Support large information collections</h3>
            <p>
              Provide a structured way to find information across substantial
              building records.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Improve on-site access</h3>
            <p>
              Give engineers and building teams access to information from
              mobile devices.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Create a foundation for AI</h3>
            <p>
              Structured and searchable building information can provide a
              foundation for AI-assisted questions and answers.
            </p>
            <div className={styles.actionLink} style={{ marginTop: "1rem" }}>
              <Link to="/ai" className={styles.textLink}>
                Explore AI →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 5. Search + AI */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/ai/ai-information.webp")}
              alt="Pulse AI alongside search"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Search + AI</span>
            <h2 className={styles.sectionHeading}>
              Find what you need. Then ask questions about it.
            </h2>
            <p className={styles.sectionCopy}>
              Search helps you locate documents, drawings and asset records
              across the building record. Pulse AI helps you understand what
              you've found by answering questions in natural language.
            </p>
            <div className={styles.actionLink}>
              <Link to="/ai" className={styles.textLink}>
                Explore AI →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 6. Final CTA */}
      <CtaSection
        eyebrow="Get Started"
        heading="Make your building information easier to find."
        copy="See how Pulse can bring documents, drawings, assets, search and AI together in one platform."
        button={{
          text: "View the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />
    </div>
  );
};

export default withPage(Search)({
  meta: globalContent.seo.search,
});
